import React, { useEffect, useState } from "react";
import { Box, Grid, Stack, Typography, LinearProgress, useTheme, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import clsx from "clsx";
import TimelineIcon from "@mui/icons-material/Timeline";
import { TargetOrderStyles } from "../styles/TargetOrder.style";
import { useTargetState } from "src/store/features/TargetOrders.slice";
import api from "src/store/features/api";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import TargetOrderProjectionsToolTip from "src/components/trade/target-orders/tooltips/TargetOrderProjectionsToolTip";
import { Fade } from "@mui/material";
interface TargetOrderProjectionsBoxProps {
  disableButton: any;
  closeToMarketError: any;
  outOfRangeError: any;
  buyTargetInvalid: boolean;
  sellTargetInValid: boolean;
  insufficientBalance: boolean;
  minSpend: boolean;
  employee?: boolean;
}
const TargetOrderProjectionsBox = (props: TargetOrderProjectionsBoxProps) => {
  const {
    disableButton,
    closeToMarketError,
    outOfRangeError,
    buyTargetInvalid,
    sellTargetInValid,
    insufficientBalance,
    minSpend,
    employee,
  } = props;

  //----------------------------REDUX------------------------------
  const { selectedToken, selectedCurrency, targetPrice, orderType, tradeAmount } = useTargetState();

  const [trigger, { isLoading: marketPriceLoading, data: selectedMarketPrice }] =
    api.endpoints.getCoinPageData.useLazyQuery();
  //----------------------------STYLES------------------------------
  const theme = useTheme();
  const classes = TargetOrderStyles();
  //----------------------------STATE------------------------------
  const [isLoading, setIsLoading] = useState(true);
  const [percentageDiff, setPercentageDiff] = useState(0);
  const [valueDifference, setValueDifference] = useState(0);
  // --------------------CONSTS---------------------------------
  const TITLE = "Price Distance" as const;
  const CLOSE_MARKET_TILE = "Target within 10% of Market Price. " as const;
  const INVALID_TITLE = "Invalid Target Price." as const;
  const MIN_SPEND_TITLE = "Target Below Minimum ";
  const ERROR_MSG_CLOSE_TARGET = `Your target price is very close to the current market price. The current market price is approximately ${formatCurrency(
    selectedMarketPrice?.price || 0,
    selectedCurrency.type === "Fiat" ? selectedCurrency.code : "USD",
  ).replace(/[a-z]/gi, "")} `;
  const INVALID_BUY_ERROR = "Your target is higher than the current market price." as const;
  const INVALID_SELL_ERROR = "Your target is lower than the current market price." as const;
  const MIN_SPEND_ERROR = `Your trade is lower than our minimum trade amount of ${
    employee ? "$50" : "$500"
  }. ` as const;

  const DEFINITION_TEXT =
    "Target distance calculates the distance in $ dollar value of your target from the market price." as const;

  const estimatedAmount =
    targetPrice &&
    tradeAmount &&
    formatCurrency(
      targetPrice * tradeAmount,
      selectedCurrency.type === "Fiat" ? selectedCurrency.code : "USD",
    );

  const INFO_MESSAGE_PART_TWO = `Your target is ${formatCurrency(
    valueDifference,
    selectedCurrency.type === "Fiat" ? selectedCurrency.code : "USD",
  )}
  away from the Market price.`;
  // ---------------------HANDLERS ------------------
  const calculateDifference = (marketPrice: number, targetPrice: number) => {
    if (marketPrice < targetPrice) {
      const percDiff = (marketPrice / targetPrice) * 100;
      const valueDifference = Math.abs(marketPrice - targetPrice);
      setValueDifference(valueDifference);
      setPercentageDiff(percDiff);
    } else {
      const percDiff = (targetPrice / marketPrice) * 100;
      const valueDifference = Math.abs(marketPrice - targetPrice);

      setValueDifference(valueDifference);
      setPercentageDiff(percDiff);
    }
  };
  // ---------------------EFFECTS ------------------
  useEffect(() => {
    if (selectedCurrency) {
      trigger({
        token: selectedToken.code,
        baseCurrency: selectedCurrency.code,
      });
    }
  }, [selectedCurrency, selectedToken]);

  useEffect(() => {
    if (targetPrice && selectedMarketPrice) {
      calculateDifference(+selectedMarketPrice.price, targetPrice);
    }
  }, [targetPrice, selectedMarketPrice]);
  useEffect(() => {
    if (
      !outOfRangeError.isOutOfRange &&
      !sellTargetInValid &&
      !minSpend &&
      !buyTargetInvalid &&
      !insufficientBalance
    ) {
      disableButton(false);
    } else {
      disableButton(true);
    }
  }, [outOfRangeError, sellTargetInValid, minSpend, buyTargetInvalid, insufficientBalance]);

  useEffect(() => {
    if (!marketPriceLoading) {
      setIsLoading(false);
    }
  }, [marketPriceLoading]);

  /*
   * Sets the correct error message.
   * @param {Object} outOfRangeError - The error object containing isOutOfRange and outOfRangeError
   * @param {Object} closeToMarketError - The error object containing isClose
   * @param {boolean} sellTargetInValid - The error flag for sell
   * @param {boolean} buyTargetInvalid - The error flag for buy
   */

  let errorMessage;
  let errorTitle;
  if (
    !outOfRangeError.isOutOfRange &&
    !closeToMarketError.isClose &&
    !sellTargetInValid &&
    !minSpend &&
    !buyTargetInvalid
  ) {
    errorTitle = TITLE;
    errorMessage = <span>{INFO_MESSAGE_PART_TWO}</span>;
  } else if (
    outOfRangeError.isOutOfRange &&
    !closeToMarketError.isClose &&
    (!buyTargetInvalid || !sellTargetInValid)
  ) {
    errorTitle = INVALID_TITLE;
    errorMessage = <span>{outOfRangeError.outOfRangeError}</span>;
  } else if (closeToMarketError.isClose && !outOfRangeError.isOutOfRange && !minSpend) {
    errorTitle = CLOSE_MARKET_TILE;
    errorMessage = <span>{ERROR_MSG_CLOSE_TARGET}</span>;
  } else if (buyTargetInvalid && orderType[0] === "Buy") {
    errorTitle = CLOSE_MARKET_TILE;
    errorMessage = <span>{INVALID_BUY_ERROR}</span>;
  } else if (sellTargetInValid && orderType[0] === "Sell") {
    errorTitle = INVALID_TITLE;
    errorMessage = <span>{INVALID_SELL_ERROR}</span>;
  } else if (minSpend) {
    errorTitle = MIN_SPEND_TITLE;
    errorMessage = <span>{MIN_SPEND_ERROR}</span>;
  } else {
    errorTitle = "Price Distance";
    errorMessage = null;
  }

  if (isLoading) {
    return (
      <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center">
        <Box className={classes.projectionBox}>
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ paddingBottom: "8px" }}
            spacing={1}>
            <Typography sx={{ fontWeight: 900 }}>Price Distance</Typography>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              fontSize: "12px",
              color: theme.palette.primary.main,
              fontWeight: 900,
              lineHeight: "14px",
            }}
            spacing={0}>
            <Typography sx={{ fontSize: "12px" }}>Calculating this for you right now...</Typography>
          </Stack>
          <LinearProgress className={classes.formProgressBar} />
        </Box>
      </Grid>
    );
  }

  return (
    <Fade in={!isLoading} timeout={{ enter: 2000, exit: 2000 }}>
      <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center">
        {insufficientBalance && orderType[0] === "Buy" && (
          <Typography className={classes.insufficientBalance} id="target-order-insufficent-balance">
            You have insufficient balance. Please deposit additional
            <Link to={"/wallets/details"} className={classes.insufficientBalanceLink}>
              {selectedCurrency.code} here
            </Link>
            or reduce the unit amount to continue.
          </Typography>
        )}
        {insufficientBalance && orderType[0] === "Sell" && (
          <Typography className={classes.insufficientBalance} id="target-order-insufficent-balance">
            You have insufficient balance. Please deposit additional
            <Link to={"/wallets/deposit"} className={classes.insufficientBalanceLink}>
              {selectedToken.code} here
            </Link>
            or reduce the unit amount to continue.
          </Typography>
        )}
        <Box
          className={clsx(
            classes.projectionBox,
            minSpend && classes.projectionBoxRange,
            outOfRangeError.isOutOfRange && classes.projectionBoxRange,
            buyTargetInvalid && classes.projectionBoxRange,
            sellTargetInValid && classes.projectionBoxRange,
            insufficientBalance && classes.projectionBoxRange,
            closeToMarketError.isClose && !minSpend && classes.projectionBoxCloseToMarket,
          )}>
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ paddingBottom: "8px" }}
            spacing={1}>
            <Typography
              id="target-order-projection-box-title"
              sx={{
                fontWeight: 900,
                fontSize: "14px",
                color: theme.palette.text.primary,
              }}>
              {errorTitle}
            </Typography>
            <TargetOrderProjectionsToolTip message={DEFINITION_TEXT} />
          </Stack>
          <Typography
            sx={{
              fontSize: "12px",
              marginBottom: "16px",
              color: "#1f2123 ",
              lineHeight: 1.17,
              fontWeight: 600,
            }}>
            {errorMessage}
          </Typography>

          <Stack direction="row" justifyContent="center" alignItems="center" mt={"8px"} spacing={0}>
            <Tooltip
              componentsProps={{
                arrow: {
                  sx: {
                    color: theme.palette.primary.main,
                  },
                },
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.primary.main,
                  },
                },
              }}
              title="Market Price"
              placement="top"
              arrow>
              <Box
                sx={{
                  height: "38px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "93px",
                  backgroundColor: "#FFF",
                  borderRadius: "8px",
                }}>
                <Stack direction="row" alignItems="center">
                  {(selectedMarketPrice?.currencyConfig.logoUrl ||
                    selectedMarketPrice?.currencyConfig.code) && (
                    <CountryOrCryptoFlag
                      name={
                        selectedMarketPrice?.currencyConfig.logoUrl ||
                        selectedMarketPrice?.currencyConfig.code
                      }
                      size={15}
                    />
                  )}
                  {selectedMarketPrice ? (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        marginLeft: "4px",
                        color: theme.palette.text.primary,
                      }}>
                      {formatCurrency(
                        +selectedMarketPrice.price,
                        selectedCurrency.type === "Fiat" ? selectedCurrency.code : "USD",
                      ).replace(/[a-z]/gi, "")}
                    </Typography>
                  ) : (
                    "$0"
                  )}
                </Stack>
              </Box>
            </Tooltip>
            <Box
              sx={{
                height: "20px",
                width: "93px",
                backgroundColor: "#FFF",
                "& .MuiLinearProgress-colorPrimary": {
                  backgroundColor: "#fff",
                },
                "& .MuiLinearProgress-barColorPrimary": {
                  backgroundColor: "#09BF81",
                },
              }}>
              <LinearProgress
                variant="determinate"
                value={percentageDiff}
                sx={{
                  height: "12px",
                  display: "absolute",
                  top: 4,
                }}
              />
            </Box>
            <Tooltip
              componentsProps={{
                arrow: {
                  sx: {
                    color: theme.palette.primary.main,
                  },
                },
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.primary.main,
                  },
                },
              }}
              title="Target Price"
              placement="top"
              arrow>
              <Box
                sx={{
                  height: "38px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "93px",
                  backgroundColor: "#FFF",
                  borderRadius: "8px",
                }}>
                <Stack direction="row" alignItems="center">
                  <TimelineIcon
                    sx={{
                      height: "12px",
                      width: "12px",
                      marginRight: "4px",
                      color: "#09BF81",
                    }}
                  />
                  <Typography sx={{ fontSize: "12px", color: "#09BF81" }}>
                    {formatCurrency(
                      targetPrice || 0,
                      selectedCurrency.type === "Fiat" ? selectedCurrency.code : "USD",
                    ).replace(/[a-z]/gi, "")}
                  </Typography>
                </Stack>
              </Box>
            </Tooltip>
          </Stack>
          {(buyTargetInvalid || sellTargetInValid) && !closeToMarketError.isClose && (
            <Typography
              sx={{
                fontSize: "12px",
                color: "#ef4444",
                fontWeight: 600,
                mt: 1,
                mb: 1,
              }}>
              Please re-enter your target or contact your broker for more details.
            </Typography>
          )}
          {!outOfRangeError.isOutOfRange && !sellTargetInValid && !buyTargetInvalid && (
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                fontSize: "12px",
                marginTop: "17px",
                color: theme.palette.primary.main,
              }}>
              <Typography sx={{ fontSize: "12px" }}>Expected output</Typography>
              <Typography sx={{ fontSize: "12px" }}>{`Approx. ${estimatedAmount}`}</Typography>
            </Stack>
          )}
        </Box>
      </Grid>
    </Fade>
  );
};

export default TargetOrderProjectionsBox;
